// Copyright © 2019-2026 Focal Systems. All rights reserved.

/**
 * The Action Tool's hand-drawn task icons, ported.
 *
 * Not every built-in task type uses a Material icon — the Action Tool's `taskIcons` map points
 * nine of them at bespoke `createSvgIcon` components in `libs/ui/src/lib/icons/index.tsx`
 * (web-focal-os). Their path data is copied verbatim here so the back office draws the same glyph
 * instead of falling back to a placeholder; only the JSX attribute names are rewritten to their
 * SVG spelling (`fillRule` → `fill-rule`) and line breaks inside path data collapsed, which is
 * lossless because SVG treats any whitespace there as a separator.
 *
 * They are keyed by the name of the component they come from, because that is what the API sends:
 * the backend decides which glyph a built-in type uses and names it the same way the Action Tool
 * does, so `TaskIcon` resolves `LowIcon` from here and `Block` from the Material set.
 *
 * All six are authored on the same 24×24 canvas `TaskIcon` renders into.
 */

/** `LowIcon` — low stock. Used by `low`, `calculatedLow` and `flexSpaceLow`. */
const LOW_ICON =
  "<path d='M0,0H24V24H0ZM0,0H24V24H0Z' transform='translate(0 0)' fill='none'/>" +
  "<path d='M20.26,9.18V6.79h-18V17.21h18V14.82h1.45V9.18Zm-1.49,6.54h-15V8.28h15Z' " +
  "transform='translate(0 0)' fill-rule='evenodd'/>" +
  "<rect x='5.3' y='9.36' width='1.49' height='5.28'/>";

/** `SpoiledIcon` — a waste bin. */
const SPOILED_ICON =
  "<path d='M19.3,3.5c-0.1-0.2-0.2-0.4-0.5-0.5l-0.1,0h-4.3V2.2l0-0.1c0-0.1-0.1-0.1-0.1-0.1l-0.1,0H9.6L9.5,2 " +
  "c-0.1,0-0.1,0.1-0.1,0.1 l0,0.1v0.8H5.1l-0.1,0C4.7,3.1,4.5,3.3,4.5,3.5l0,0.1v2h14.9v-2L19.3,3.5z'/>" +
  "<path d='M6.2,21l0,0.2C6.3,21.6,6.6,21.9,7,22l0.2,0h9.4l0.2,0c0.4-0.1,0.7-0.4,0.8-0.8l0-0.2l0.7-14.1H5.5L6.2,21z " +
  "M14,10.4l0-0.1 c0-0.1,0.1-0.2,0.3-0.3l0.1,0h0.7c0.2,0,0.3,0.1,0.4,0.3l0,0.1v8.1l0,0.1c0,0.1-0.1,0.2-0.3,0.3l-0.1,0h-0.7" +
  "l-0.1,0 c-0.1,0-0.2-0.1-0.3-0.3l0-0.1V10.4z M11.2,10.4l0-0.1c0-0.1,0.1-0.2,0.3-0.3l0.1,0h0.7c0.2,0,0.3,0.1,0.4,0.3l0," +
  "0.1v8.1l0,0.1 c0,0.1-0.1,0.2-0.3,0.3l-0.1,0h-0.7l-0.1,0c-0.1,0-0.2-0.1-0.3-0.3l0-0.1V10.4z M8.3,10.4l0-0.1c0-0.1,0.1-" +
  "0.2,0.3-0.3l0.1,0h0.7 c0.2,0,0.3,0.1,0.4,0.3l0,0.1v8.1l0,0.1c0,0.1-0.1,0.2-0.3,0.3l-0.1,0H8.7l-0.1,0c-0.1,0-0.2-0.1-" +
  "0.3-0.3l0-0.1V10.4z'/>";

/** `ManualTaskIcon` — a framed plus sign. */
const MANUAL_TASK_ICON =
  "<path d='m 5.000025,21 c -1.2048386,0.04459 -2.1596631,-1.142858 -2,-2.30547 0.00519,-4.125725 -0.010388," +
  "-8.251895 0.00781,-12.3773425 C 3.1432429,5.3229983 3.9923937,4.6833233 4.5504311,3.9137926 4.9220991," +
  "3.3877588 5.4520282,2.8996417 6.1527599,3 10.159062,3.00568 14.165931,2.988618 18.171878,3.00856 c 0.873558," +
  "0.077965 1.244008,0.9605614 1.787219,1.5239425 0.539999,0.6286568 1.159622,1.325562 1.040878,2.2220526 " +
  "-0.0061,4.1492609 0.01218,8.2990729 -0.0092,12.4479889 C 20.9128,20.327671 19.784282,21.141322 18.694505,21 " +
  "14.129679,21 9.5648518,21 5.000025,21 Z m 0.4,-15 c 4.3999833,0 8.799967,0 13.19995,0 -0.283333,-0.3333333 " +
  "-0.566667,-0.6666667 -0.85,-1 -3.833317,0 -7.666633,0 -11.49995,0 -0.2833333,0.3333333 -0.5666667,0.6666667 " +
  "-0.85,1 z m -0.4,2 c 0,3.666667 0,7.333333 0,11 4.66665,0 9.3333,0 13.99995,0 0,-3.666667 0,-7.333333 0,-11 " +
  "-4.66665,0 -9.3333,0 -13.99995,0 z m 6.04995,8.5 c 0.666667,0 1.333333,0 2,0 0,-0.654333 0,-1.308667 0,-1.963 " +
  "0.533333,0 1.066667,0 1.6,0 0,-0.633333 0,-1.266667 0,-1.9 -0.533333,0 -1.066667,0 -1.6,0 0,-0.545667 0," +
  "-1.091333 0,-1.637 -0.666667,0 -1.333333,0 -2,0 0,0.545667 0,1.091333 0,1.637 -0.533333,0 -1.0666667,0 -1.6,0 " +
  "0,0.633333 0,1.266667 0,1.9 0.5333333,0 1.066667,0 1.6,0 0,0.654333 0,1.308667 0,1.963 z'/>";

/** `MissingTagIcon` — a shelf tag with a plus. */
const MISSING_TAG_ICON =
  "<path d='M21,12l-4.37,6.16C16.26,18.68,15.65,19,15,19h-3l0-2h3l3.55-5L15,7H5v3H3V7c0-1.1,0.9-2,2-2h10" +
  "c0.65,0,1.26,0.31,1.63,0.84 L21,12z M10,15H7v-3H5v3H2v2h3v3h2v-3h3V15z'/>";

/** `ExtraTagIcon` — a shelf tag with a minus. */
const EXTRA_TAG_ICON =
  "<path d='m 21,12 -4.37,6.16 C 16.26,18.68 15.65,19 15,19 h -3 v -2 h 3 L 18.55,12 15,7 H 5 v 3 H 3 V 7 " +
  "C 3,5.9 3.9,5 5,5 h 10 c 0.65,0 1.26,0.31 1.63,0.84 z m -11,3 c 0,0 -2.1395069,0 -8,0 v 2 h 8 z'/>";

/** `PersonWithSquare` — manual gap scan. Authored on a 20×20 grid inside the 24×24 canvas. */
const PERSON_WITH_SQUARE_ICON =
  "<path fill-rule='evenodd' clip-rule='evenodd' d='M9.07409 3.51854C10.0926 3.51854 10.9259 4.35187 10.9259 " +
  "5.37039C10.9259 6.38891 10.0926 7.22224 9.07409 7.22224C8.05558 7.22224 7.22224 6.38891 7.22224 5.37039C7.22224 " +
  "4.35187 8.05558 3.51854 9.07409 3.51854ZM9.07409 10.9259V12.7778C6.58335 12.7778 3.7315 13.963 3.51854 " +
  "14.6297H9.0741V16.4815H1.66669V14.6297C1.66669 12.1667 6.60187 10.9259 9.07409 10.9259ZM5.37039 5.37039C5.37039 " +
  "7.41669 7.0278 9.07409 9.07409 9.07409C11.1204 9.07409 12.7778 7.41669 12.7778 5.37039C12.7778 3.32409 11.1204 " +
  "1.66669 9.07409 1.66669C7.0278 1.66669 5.37039 3.32409 5.37039 5.37039ZM10.9259 10.9259H18.3334L18.3334 " +
  "18.3334H10.9259V10.9259ZM12.7778 16.4815H16.4815V12.7778H12.7778V16.4815Z'/>";

/** The hand-drawn glyphs, under the component name the Action Tool exports them as. */
export const ACTION_TOOL_GLYPHS: Record<string, string> = {
  LowIcon: LOW_ICON,
  SpoiledIcon: SPOILED_ICON,
  ManualTaskIcon: MANUAL_TASK_ICON,
  PersonWithSquare: PERSON_WITH_SQUARE_ICON,
  MissingTagIcon: MISSING_TAG_ICON,
  ExtraTagIcon: EXTRA_TAG_ICON,
};
